import {
  BasicFormItem,
  BasicFormItemProps,
  Button,
  Column,
  Divider,
  EmptySearchState,
  InputWithAddon,
  ModalBase,
  Row,
  SelectedFile,
  Typography,
} from '@/components'
import { SearchContainer } from '@/components/organisms/AddInsuranceView/styles'
import { CardNode } from '@/components/organisms/GroupCardSelectWithModal/CardNode'
import {
  ButtonsContainer,
  CarrierContainer,
  Container,
  ContentWrapper,
  InsuranceGroup,
  SelectedCardTop,
  SelectedInsurancesGroup,
  SelectedStyledCard,
  StyledChecked,
  TitleContainer,
} from '@/components/organisms/GroupCardSelectWithModal/styles'
import { Breakpoint, ThemeColor } from '@/enums'
import { SearchIcon } from '@/icons'
import { InsuranceCarrierStep } from '@/modules/addInsurances/components/steps/InsuranceCarrierStep'
import { PolicyNumberStep } from '@/modules/addInsurances/components/steps/PolicyNumberStep'
import { UploadDocumentsStep } from '@/modules/addInsurances/components/steps/UploadDocumentsStep'
import {
  StepperProvider,
  useStepper as useAddInsuranceStepper,
} from '@/modules/addInsurances/hooks/useStepper'
import { useInsuranceGroups } from '@/modules/insurances/hooks/useInsuranceGroups'
import { useCurrentInsuranceMessages } from '@/pages/SelfAssessmentPage/steps/CurrentInsurancesStep'
import { useSnackbar } from '@/providers'
import { OutletCarrier } from '@/types/carrier.type'
import { themeBreakpointDown, toThemePixels } from '@/utils'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useOutletContext } from 'react-router-dom'
import styled from 'styled-components'

interface InsuranceOption {
  value: string
  title: string
  subtitle: string
}

interface SelectedInsurance {
  value: string
  title: string
  subtitle: string
  provider: string
  id: number
  files: SelectedFile[]
  categoryId: string
  carrierId: string
  policyNumber: string | null
  providers: string[]
}

interface SupplementaryInsuranceInformation {
  carrierId?: string
  categoryId?: string
  policyNumber?: string | null
  files?: SelectedFile[]
}

interface ExportData {
  carrierId: string
  categoryId: string
  policyNumber: string | null
  files: File[]
}

const SelectedProvidersWrapper = styled(Column)`
  padding: ${toThemePixels(4)};
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-direction: column;
  gap: ${toThemePixels(2)};
`

const SelectedProvider = styled(Row)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`

const StepperContainer = styled(Column)`
  height: 85vh;
  width: 700px;

  ${themeBreakpointDown(Breakpoint.md)} {
    width: 600px;
  }

  ${themeBreakpointDown(Breakpoint.sm)} {
    height: 96vh;
    width: 100%;
  }
`

const makeSteps = () => [
  InsuranceCarrierStep,
  PolicyNumberStep,
  UploadDocumentsStep,
]

const BaseStepper = () => {
  const { steps, currentStep } = useAddInsuranceStepper()
  const CurrentStep = steps[currentStep]

  return <StepperContainer>{<CurrentStep />}</StepperContainer>
}

interface SelectedCardNodeProps {
  item: SelectedInsurance
  removeProvider: (insuranceValue: string, providerValue: string) => void
}

const SelectedCardNode = ({ item, removeProvider }: SelectedCardNodeProps) => {
  const { title, subtitle, value, providers } = item
  const { t } = useTranslation()

  const { carriers } = useOutletContext<{ carriers: OutletCarrier[] }>()

  const findListItems = () =>
    providers.map((provider) =>
      carriers.find((option: OutletCarrier) => option.value === provider),
    )

  const providerItems = findListItems()

  return (
    <SelectedStyledCard>
      <SelectedCardTop>
        <TitleContainer>
          <Typography variant="captionB">{title}</Typography>
          <Typography color={ThemeColor.b50} variant="p2Body">
            {subtitle}
          </Typography>
        </TitleContainer>
        <StyledChecked />
      </SelectedCardTop>
      <SelectedProvidersWrapper>
        {providerItems.map((provider) => (
          <SelectedProvider key={provider?.value}>
            <CarrierContainer>
              {provider?.icon && (
                <img alt="provider icon" src={provider.icon} />
              )}
              <Typography color={ThemeColor.b50} variant="captionR">
                {provider?.text}
              </Typography>
            </CarrierContainer>
            <ButtonsContainer>
              <div />
              {provider && (
                <Button
                  color="danger"
                  onClick={() => removeProvider(value, provider.value)}
                  size="medium"
                  variant="outlined"
                >
                  {t('common.remove')}
                </Button>
              )}
            </ButtonsContainer>
          </SelectedProvider>
        ))}
      </SelectedProvidersWrapper>
    </SelectedStyledCard>
  )
}

interface InsuranceAndProviderSelectorProps {
  onChange: (data: ExportData[]) => void
}

const InsuranceAndProviderSelector = ({
  onChange,
}: InsuranceAndProviderSelectorProps) => {
  const { t } = useTranslation()
  const [selectedInsurances, setSelectedInsurances] = useState<
    SelectedInsurance[]
  >([])
  const [modalOpen, setModalOpen] = useState(false)
  const [
    currentSelection,
    setCurrentSelection,
  ] = useState<InsuranceOption | null>(null)
  const [searchTerm, setSearchTerm] = useState('')

  const cardOptions = useInsuranceGroups()
  const messages = useCurrentInsuranceMessages()

  const toggleModal = (
    isOpen: boolean,
    insurance: InsuranceOption | null = null,
  ) => {
    setModalOpen(isOpen)
    setCurrentSelection(insurance)
  }

  const removeProviderFromInsurance = (
    insuranceValue: string,
    providerValue: string,
  ) => {
    setSelectedInsurances((prev) =>
      prev.reduce<SelectedInsurance[]>((acc, cur) => {
        if (cur.value === insuranceValue) {
          if (cur.provider === providerValue) {
            return acc
          }
        }
        acc.push(cur)
        return acc
      }, []),
    )
  }

  const exportSelectedData = (): ExportData[] =>
    selectedInsurances.map((ins) => ({
      carrierId: ins.provider,
      categoryId: ins.value,
      policyNumber: ins.policyNumber,
      files: (ins.files || []).map(({ file }) => file),
    }))

  const snackbar = useSnackbar()

  const supplementInsuranceInformation = (
    insuranceInfo: SupplementaryInsuranceInformation,
  ) => {
    snackbar.success(messages.contractAdded, 2000)
    setSelectedInsurances((prev) => {
      const exists = prev.some(
        (ins) =>
          ins.value === currentSelection?.value &&
          ins.provider === insuranceInfo.carrierId,
      )

      if (!exists && currentSelection) {
        return [
          ...prev,
          {
            ...currentSelection,
            id: Date.now(),
            provider: insuranceInfo.carrierId,
            providers: [],
            ...insuranceInfo,
          } as SelectedInsurance,
        ]
      }

      return prev
    })

    toggleModal(false)
    setCurrentSelection(null)
    setSearchTerm('')
  }

  const handleInsuranceClick = (insurance: InsuranceOption) => {
    toggleModal(true, insurance)
  }

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value)
  }

  const filteredCardOptions = cardOptions.filter((group) =>
    group.items.some((item) =>
      item.title?.toLowerCase().startsWith(searchTerm.toLowerCase()),
    ),
  )

  const groupInsurancesByType = (insurances: SelectedInsurance[]) =>
    insurances.reduce<SelectedInsurance[]>((acc, cur) => {
      const found = acc.find((ins) => ins.value === cur.value)
      if (found) {
        found.providers.push(cur.provider)
      } else {
        acc.push({ ...cur, providers: [cur.provider] })
      }
      return acc
    }, [])

  useEffect(() => {
    onChange([...exportSelectedData()])
  }, [selectedInsurances])

  const groupedInsurances = groupInsurancesByType(selectedInsurances)

  return (
    <Container>
      <ContentWrapper>
        <SearchContainer>
          <InputWithAddon
            addonBefore={() => <SearchIcon color={ThemeColor.b50} />}
            onChange={handleSearchChange}
            rounded
            value={searchTerm}
          />
          <Divider color={ThemeColor.b30} />
        </SearchContainer>
        {!searchTerm && groupedInsurances.length > 0 && (
          <SelectedInsurancesGroup>
            <Typography bold color={ThemeColor.b50} variant="badgeText">
              {t('selected')}
            </Typography>
            {groupedInsurances.map((item: SelectedInsurance) => (
              <SelectedCardNode
                key={item.id}
                item={item}
                removeProvider={removeProviderFromInsurance}
              />
            ))}
          </SelectedInsurancesGroup>
        )}
        {filteredCardOptions.length ? (
          filteredCardOptions.map(({ groupName, items }) => (
            <InsuranceGroup key={groupName}>
              <Typography bold color={ThemeColor.b50} variant="badgeText">
                {groupName}
              </Typography>
              {items.map((insurance) => (
                <CardNode
                  key={insurance.value}
                  onClick={() => handleInsuranceClick(insurance)}
                  subtext={insurance.subtitle}
                  text={insurance.title}
                  value={insurance.value}
                />
              ))}
            </InsuranceGroup>
          ))
        ) : (
          <EmptySearchState search={searchTerm} />
        )}
        {modalOpen && (
          <ModalBase
            closable
            enableClickOutside={false}
            isOpen={modalOpen}
            onClose={() => toggleModal(false)}
            shouldRenderInPortal
            padding="24px 16px 0 16px"
          >
            <StepperProvider
              defaultValues={
                currentSelection
                  ? {
                      categoryId: currentSelection.value,
                    }
                  : {}
              }
              onFinish={(values) => supplementInsuranceInformation(values)}
              makeSteps={makeSteps}
            >
              <BaseStepper />
            </StepperProvider>
          </ModalBase>
        )}
      </ContentWrapper>
    </Container>
  )
}

type InsuranceAndProviderSelectorFormFieldProps = Omit<
  BasicFormItemProps,
  'component' | 'name'
>

const InsuranceAndProviderSelectorFormField = (
  props: InsuranceAndProviderSelectorFormFieldProps,
) => (
  <BasicFormItem
    {...props}
    name="insurances"
    component={InsuranceAndProviderSelector}
  />
)

export default InsuranceAndProviderSelectorFormField
